import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, useDroppable, useDraggable,
  PointerSensor, useSensor, useSensors, closestCorners,
} from '@dnd-kit/core';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { talentApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

// 9-box layout, top row = high potential. box = (potential-1)*3 + performance.
const ROWS = [3, 2, 1]; // potential, top→bottom
const COLS = [1, 2, 3]; // performance, left→right
const boxOf = (perf, pot) => (pot - 1) * 3 + perf;
const BOX_LABELS = {
  1: 'Risk', 2: 'Average', 3: 'Solid', 4: 'Inconsistent', 5: 'Core',
  6: 'High Performer', 7: 'Potential Gem', 8: 'High Potential', 9: 'Star',
};

/**
 * 9-box calibration grid. Drag an employee card between boxes (reuses @dnd-kit +
 * the e2e drag helper). The distribution chart updates as cards move; finalizing
 * locks the session (further moves rejected with 422).
 */
export default function CalibrationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const didDragRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    talentApi
      .getSession(id)
      .then((d) => setData(d))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);
  useEffect(() => { hrApi.listEmployees().then((e) => setEmployees(e?.items || e || [])).catch(() => setEmployees([])); }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const finalized = data?.session?.status === 'finalized';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ratings = data?.ratings || [];
  const inBox = (n) => ratings.filter((r) => Number(r.box) === n);
  const unplaced = ratings.filter((r) => r.box == null);

  const onDragStart = useCallback((e) => { setActiveId(e.active.id); didDragRef.current = false; }, []);
  const onDragMove = useCallback(() => { didDragRef.current = true; }, []);
  const onDragEnd = useCallback(async (e) => {
    setActiveId(null);
    if (!e.over || !data) return;
    const employeeId = e.active.id;
    const overId = String(e.over.id);
    const box = overId.startsWith('calbox-') ? Number(overId.slice('calbox-'.length)) : null;
    if (!box) return;
    const r = ratings.find((x) => x.employee_id === employeeId);
    if (!r || Number(r.box) === box) return;

    // Optimistic move.
    setData((prev) => ({
      ...prev,
      ratings: prev.ratings.map((x) => (x.employee_id === employeeId ? { ...x, box } : x)),
    }));
    try {
      await talentApi.upsertRating(id, { employeeId, box });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not place (session may be finalized)');
      load();
    }
  }, [data, ratings, id, load]);

  async function addParticipant(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const employeeId = fd.get('employeeId');
    if (!employeeId) return;
    try {
      await talentApi.addParticipant(id, employeeId);
      e.target.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add participant');
    }
  }

  async function finalize() {
    try {
      await talentApi.finalize(id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to finalize');
    }
  }

  if (loading && !data) return <div className="muted" data-testid="talent-calibration-detail-page" data-loading="true">Loading…</div>;
  if (!data) return <div className="alert error">Session not found</div>;

  const chartData = Array.from({ length: 9 }, (_, i) => ({ box: String(i + 1), count: (data.distribution?.counts || {})[i + 1] || 0 }));
  const dragging = activeId ? ratings.find((r) => r.employee_id === activeId) : null;

  return (
    <section data-testid="talent-calibration-detail-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>{data.session.name}</h1>
          <p className="muted">
            <span data-testid="talent-calibration-detail-status">{data.session.status}</span> · {data.distribution?.placed || 0} placed · {unplaced.length} unplaced
          </p>
        </div>
        <div className="actions">
          <Link to="/talent/calibration" className="btn ghost">Back</Link>
          {!finalized && <button className="btn primary" data-testid="talent-calibration-finalize" onClick={finalize}>Finalize &amp; lock</button>}
        </div>
      </header>

      {finalized && <div className="alert" data-testid="talent-calibration-locked">This session is finalized — ratings are locked.</div>}
      {error && <div className="alert error" data-testid="talent-calibration-detail-error">{error}</div>}

      {!finalized && (
        <form onSubmit={addParticipant} className="card form-card grid-form">
          <label>Add participant
            <select name="employeeId" data-testid="talent-participant-employee-input" required defaultValue="">
              <option value="" disabled>— select —</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
            </select>
          </label>
          <div className="actions"><button className="btn" type="submit" data-testid="talent-participant-add">Add</button></div>
        </form>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
        <div className="card">
          <h3>Unplaced</h3>
          <BoxCell n={null} testid="calibration-unplaced" label="Unplaced" cards={unplaced} finalized={finalized} didDragRef={didDragRef} />
        </div>

        <div className="card">
          <div className="ninebox-grid" data-testid="calibration-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {ROWS.map((pot) => COLS.map((perf) => {
              const n = boxOf(perf, pot);
              return <BoxCell key={n} n={n} testid={`calibration-box-${n}`} label={`${n} · ${BOX_LABELS[n]}`} cards={inBox(n)} finalized={finalized} didDragRef={didDragRef} />;
            }))}
          </div>
          <p className="muted" style={{ marginTop: 8 }}>← performance →&nbsp;&nbsp;·&nbsp;&nbsp;↑ potential ↑</p>
        </div>

        <DragOverlay>{dragging && <Card r={dragging} dragging />}</DragOverlay>
      </DndContext>

      <div className="card">
        <h3>Distribution</h3>
        <div data-testid="talent-distribution-chart" style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="box" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function BoxCell({ n, testid, label, cards, finalized, didDragRef }) {
  const dropId = n == null ? 'caltray' : `calbox-${n}`;
  const { isOver, setNodeRef } = useDroppable({ id: dropId });
  return (
    <div
      ref={setNodeRef}
      className={`kcol ${isOver ? 'kcol-over' : ''}`}
      data-testid={testid}
      data-box={n ?? 'unplaced'}
      style={{ minHeight: 90, padding: 8, border: '1px solid var(--border,#e2e8f0)', borderRadius: 8 }}
    >
      <header data-testid="box-header"><span className="kcol-name">{label}</span><span className="kcol-count">{cards.length}</span></header>
      <div className="kcol-body">
        {cards.map((r) => <DraggableCard key={r.employee_id} r={r} finalized={finalized} didDragRef={didDragRef} />)}
        {cards.length === 0 && <div className="kcol-empty muted">—</div>}
      </div>
    </div>
  );
}

function DraggableCard({ r, finalized, didDragRef }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: r.employee_id, disabled: finalized });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : {};
  const { role: _r, tabIndex: _t, ...dragAttrs } = attributes;
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`calibration-card-${r.employee_id}`}
      data-employee-id={r.employee_id}
      data-box={r.box ?? 'unplaced'}
      {...(finalized ? {} : dragAttrs)}
      {...(finalized ? {} : listeners)}
    >
      <Card r={r} didDragRef={didDragRef} />
    </div>
  );
}

function Card({ r }) {
  return (
    <div className="issue-card">
      <div className="issue-title">{r.employee_name || r.employee_id}</div>
      <footer><span className="chip">{r.employee_code || ''}</span></footer>
    </div>
  );
}
